/**
 * Core type definitions for Tower Challenger application
 */

// ============================================================================
// Organizer Types
// ============================================================================

export interface Organizer {
  id: string;
  name: string;
  slug: string; // Unique identifier for URLs
  logo?: string; // URL to logo image
  description?: string; // Optional description of the organizer
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  subscriptionTier: "basic" | "competitive" | "premium";
}

export interface OrganizerTournament extends Tournament {
  teamCount: number; // Number of teams in the tournament
  locations: Location[]; // Locations associated with the tournament
  matches: Match[]; // Matches associated with the tournament
  playersCount: number; // Number of players associated with the tournament
}

// ============================================================================
// User Types
// ============================================================================

// Notification preferences for push notifications
export interface NotificationPreferences {
  globalTournamentNotifications?: boolean; // Team joins, team vacations, member joins
  organizerNotifications?: boolean; // Organizer announcements
  otherTeamMatchScheduled?: boolean; // Other teams' match scheduled
  otherTeamChallenges?: boolean; // Other teams' challenges
  myTeamChallenges?: boolean; // My team's challenges, match scheduling, results
  applicationNotifications?: boolean; // App-wide messages from administrators
  tournamentChatNotifications?: boolean; // New messages in tournament-wide chat
  matchChatNotifications?: boolean; // New messages in any match chat
  teamChatNotifications?: boolean; // New messages in the team-private chat
  poolRequestNotifications?: boolean; // Player-pool interest requests and team-formed events
  // Email opt-outs (transactional emails). Undefined/true = receive; false = opted out.
  emailNotificationsEnabled?: boolean; // Master switch — false disables ALL transactional emails
  emailChallengeNotifications?: boolean; // Email when your team receives a new challenge (and the half-time scheduling reminder)
  emailMatchScheduled?: boolean; // Email when a match is scheduled or rescheduled
  // Marketing consent (newsletters / product updates). Independent of the
  // transactional switches above. Undefined/true = subscribed; false = opted out.
  marketingEmailsEnabled?: boolean;
}

// Public profile - accessible to all authenticated users (stored in publicProfiles collection)
export interface PublicUser {
  id: string; // Firebase Auth UID
  name: string;
  createdAt: Date;
  updatedAt: Date;
  photoURL: string;
  tournamentIds: Tournament["id"][];
  language?: "en" | "fi"; // User's preferred language for emails and notifications
  feedbackPopupShownAt?: Date; // When feedback popup was shown to user
  lastFeedbackSentAt?: Date; // When user last submitted feedback (for flood prevention)
  lastActiveAt?: Date; // Last time user was active (for presence tracking)
}

// Private user data - only accessible to the user themselves (stored in users collection)
export type PrivateUser = {
  id: string; // Firebase Auth UID
  superAdmin: boolean; // Access to everything
  organizerId?: Organizer["id"]; // Organizer ID the user is associated with (optional - not all users are organizers)
  organizerName?: string; // Organizer name (fetched from organizer document)
  organizerSlug?: string; // Organizer slug (fetched from organizer document)
  email: string;
  createdAt: Date;
  updatedAt: Date;
  fcmTokens?: string[]; // Firebase Cloud Messaging tokens for push notifications (private for security)
  pushNotificationsEnabled?: boolean; // Whether user has enabled push notifications
  notificationPreferences?: NotificationPreferences; // User's notification preferences (private)
};

// Combined type for current user (has both public and private data)
export type User = PublicUser & PrivateUser;

// ============================================================================
// Sport Types
// ============================================================================

export interface Sport {
  id: string;
  name: string;
  slug: string;
}

// ============================================================================
// Statistics Types
// ============================================================================

export interface TeamStatistics {
  teamId: string;
  wins: number;
  losses: number;
  challengesSent: number;
  challengesReceived: number;
  gamesWon: number;
  gamesLost: number;
  setsWon: number;
  setsLost: number;
  tiebreaksWon: number;
  tiebreaksLost: number;
  rankingHistory: TeamRankingHistoryEntry[];
  winRate: number;
}

export interface TournamentAverageStatistics {
  tournamentId: string;
  averageWins: number;
  averageLosses: number;
  averageChallengesSent: number;
  averageChallengesReceived: number;
  averageGamesWon: number;
  averageGamesLost: number;
  averageSetsWon: number;
  averageSetsLost: number;
  averageTiebreaksWon: number;
  averageTiebreaksLost: number;
  totalTeams: number;
}

// ============================================================================
// Location Types
// ============================================================================

export interface GeoLocation {
  lat: number;
  lng: number;
  address?: string;
  city?: string;
  postal?: string;
}

export interface LocationStriimyConfig {
  enabled: boolean;
  venueId: string; // Smashflix venueId this location streams through
  apiKeySecretName?: string; // Secret Manager name ("striimy-api-key-${locationId}")
  apiKeyHint?: string; // Last 4 chars of the raw key, for UI display only
  courts: number[]; // Arbitrary court numbers, e.g. [1, 3, 4, 6, 18]
}

export interface Location {
  id: string;
  organizerId: string;
  name: string; // e.g., "Allin halli"
  details?: string; // e.g., "Kenttä 2"
  geoLocation?: GeoLocation; // Geographic coordinates and address info
  striimy?: LocationStriimyConfig; // Optional Striimy streaming integration
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Tournament Types
// ============================================================================

export interface Tournament {
  id: string;
  slug: string; // Unique identifier for URLs
  organizerId: Organizer["id"];
  locationId: Location["id"];
  name: string;
  sport: Sport["slug"];
  description: string;
  logo?: string; // URL to logo image
  headerImage?: string; // URL to tournament header image
  startsAt: Date;
  endsAt: Date;
  locationIds: Location["id"][]; // Array of location IDs
  isActive: boolean;
  archived: boolean; // Manually archived tournament (hidden from active list)
  tier: "basic" | "competitive" | "premium"; // Subscription tier for this tournament
  createdAt: Date;
  updatedAt: Date;

  challengeExpiryDays: number; // in days
  maxVacationPeriods: number; // in days
  vacationProtectionDays?: number; // Days before a team's vacation starts during which it cannot be challenged (default 7)
  challengeCooldownHours: number; // Hours after scheduled match start time before team can challenge again
  defendCooldownHours: number; // Hours after scheduled match start time before defended team can challenge again

  // Sign-in period
  signInStartsAt?: Date; // When sign-in opens
  signInEndsAt?: Date; // When sign-in closes

  towerTeamsPerRow: number; // Number of teams per row in tower (5 = Tower, 2 = Ladder)
  towerTopRowTeamCount: number; // Number of teams on the top row (1 or 2)

  // Challenge rules
  preventLoserRematch?: boolean; // If true, losing defender cannot re-challenge winner until either plays a different opponent

  // Join rules
  allowJoinDuringTournament?: boolean; // If true (default — undefined treated as true), teams can join while the tournament is active and are placed at the bottom of the tower

  // Team size
  maxPlayersPerTeam: number; // Maximum number of players allowed per team

  // Custom rules
  braveheartCards: number; // max braveheart cards per team
  braveheartResponseHours: number; // Hours team2 has to decline a braveheart challenge before auto-accepted
  jokerCards: number; // max joker cards per team

  // Included teams for convenience
  teams?: Team[];

  // Optional Striimy competition this tournament's streams are tagged with.
  // `competitionRef` is the token from Striimy (`league:id` | `tournament:id` |
  // `event:id`); `competitionName` is denormalized for display in the form.
  striimy?: {
    competitionRef?: string;
    competitionName?: string;
  };

  // Throttle stamp for tournament chat push notifications (see actions/tournamentChat.ts).
  lastChatNotificationAt?: Date;
}

// ============================================================================
// Team Types
// ============================================================================

export enum TeamStatus {
  AVAILABLE = "available",
  CHALLENGE = "challenge",
  MATCH_SCHEDULED = "match_scheduled",
  ON_VACATION = "on_vacation",
  INJURED = "injured",
  JOIN_REQUEST = "join_request",
  DISABLED = "disabled",
  FORFEIT = "forfeit",
}

export interface VacationPeriod {
  id: string;
  start: Date;
  end: Date;
}

export interface Team {
  id: string;
  tournamentId: string;
  name?: string;
  playerIds: string[]; // Limited by tournament.maxPlayersPerTeam
  captainId: string; // PublicUser ID of the captain
  ranking: number; // Weight/score for sorting (lower = better, can be any number)
  calculatedPosition?: number; // Actual position in sorted array (1-based, calculated at runtime)
  jokerCards: number; // Number of unused joker cards
  braveheartCards: number; // Number of unused cards
  status: TeamStatus;
  createdAt: Date;
  updatedAt: Date;
  vacations?: VacationPeriod[]; // Array of vacation periods
  injuredFrom?: Date;
  isActive: boolean;
  isPlacedInTower: boolean; // false = in team pool, true = placed in tower
  lastMatchScheduledAt?: Date; // Track when last match was scheduled (for cooldown - only set for challenger)
  lastDefendMatchScheduledAt?: Date; // Track when last match was scheduled as defender (for defend cooldown)
}

export type TeamRankingHistoryEntry = {
  date: Date;
  previousRanking: number;
  newRanking: number;
  previousTowerLevel: number;
  newTowerLevel: number;
};

// ============================================================================
// Match Types
// ============================================================================

export enum MatchStatus {
  CHALLENGE = "challenge", // Challenge sent, waiting for scheduling
  SCHEDULED = "scheduled", // Match time set
  COMPLETED = "completed", // Match finished with results
  CANCELLED = "cancelled", // Challenge cancelled/expired
  DECLINED = "declined", // Braveheart challenge was declined by challenged team
}

export enum ChallengeType {
  JOKER = "joker", // Challenge 2 levels up
  BRAVEHEART = "braveheart", // Challenge any team
}

export type ReactionType = string; // Emoji character (e.g. "👍", "🤗", "💩")

export type MatchStreamingStatus = "scheduled" | "failed" | "cancelled";

export interface MatchStreaming {
  streamId?: string; // Present when status === "scheduled" or "cancelled"; absent on "failed"
  streamUrl?: string;
  courtNumber: number;
  venueId: string;
  status: MatchStreamingStatus;
  error?: string;
  requestedAt: Date;
}

export interface Match {
  id: string;
  tournamentId: string;
  team1Id: string; // Challenging team
  team2Id: string; // Challenged team
  locationId?: Location["id"] | null; // Where the match will be played
  matchDate?: Date | null; // When the match is scheduled
  status: MatchStatus;
  result?: SportMatchResult | null; // Embedded match result
  winnerId?: string | null; // Team ID of the winner (set when match is completed)
  challengeExpiresAt: Date; // 24 hours after challenge creation
  createdAt: Date;
  updatedAt: Date;
  challengeType?: ChallengeType;
  braveheartResponseDeadline?: Date; // Deadline for declining a braveheart challenge
  sport: Sport["slug"];
  initiatedByUserId: User["id"]; // User ID who created the challenge
  reminderSent?: boolean; // Whether match result reminder email has been sent
  expiryWarningSent?: boolean; // Whether expiry warning email has been sent
  expiryWarningSentAt?: Date; // When expiry warning was sent
  scheduleReminderSent?: boolean; // Whether schedule reminder email has been sent
  scheduleReminderSentAt?: Date; // When schedule reminder was sent
  braveheartHalfwayReminderSent?: boolean; // Braveheart: halfway-to-deadline reminder dispatched
  braveheartHalfwayReminderSentAt?: Date;
  braveheartFinalReminderSent?: boolean; // Braveheart: ~12h-before-deadline reminder dispatched (or pre-set when short window)
  braveheartFinalReminderSentAt?: Date;
  reactions?: Record<string, ReactionType>; // { [userId]: reactionType }
  streaming?: MatchStreaming; // Set when the match is booked for Striimy streaming
  rescheduleCount?: number; // Incremented each time rescheduleMatch() runs; undefined means 0
}

// ============================================================================
// Match Result Types (Padel specific)
// ============================================================================

export interface PadelSetScore {
  team1Games: number;
  team2Games: number;
  tieBreak?: {
    team1Points: number;
    team2Points: number;
  };
  // True when this set was played as a direct super-tiebreak (no games);
  // team1Games/team2Games are recorded as 1-0 / 0-1 from the tiebreak winner.
  isTieBreakOnly?: boolean;
}

export interface PadelTieBreakScore {
  team1Points: number;
  team2Points: number;
}

export type MatchResult = {
  id: string;
  matchId: string;
  tournamentId: string;
  winnerId?: string; // Team ID of the winner
  walkover?: boolean; // True if match was decided by walkover/forfeit
  createdAt: Date;
  updatedAt: Date;
  sport: Sport["slug"];
};

// Generic match result type (for future sports)
export type PadelMatchResult = MatchResult & {
  set1: PadelSetScore;
  set2: PadelSetScore;
  set3: PadelSetScore;
  tieBreak?: PadelTieBreakScore; // Only if sets are 1-1
};

// ============================================================================
// Match Result Types (Pickleball specific)
// ============================================================================

export interface PickleballGameScore {
  team1Points: number;
  team2Points: number;
}

export type PickleballMatchResult = MatchResult & {
  // 1–5 entries; first team to 3 game-wins wins the match
  games: PickleballGameScore[];
};

export type SportMatchResult = PadelMatchResult | PickleballMatchResult;

// ============================================================================
// Invitation Types
// ============================================================================

export enum InvitationStatus {
  PENDING = "pending",
  ACCEPTED = "accepted",
  DECLINED = "declined",
  CANCELLED = "cancelled",
}

export interface Invitation {
  id: string;
  teamId: Team["id"];
  tournamentId: Tournament["id"];
  email: string;
  status: InvitationStatus;
  invitedBy: PublicUser["id"]; // User who sent the invitation
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Player Pool Types
// ============================================================================

export type SkillLevel = "beginner" | "intermediate" | "advanced" | "elite";

export const SKILL_LEVELS: SkillLevel[] = [
  "beginner",
  "intermediate",
  "advanced",
  "elite",
];

export type PoolEntryStatus = "active" | "matched" | "left";
export type PoolRequestStatus =
  | "pending"
  | "accepted"
  | "ignored"
  | "superseded"
  | "cancelled";

// Collection: /tournamentPoolEntries/{id}
// One entry per (user, tournament) — created when a user joins as a free player.
export interface TournamentPoolEntry {
  id: string;
  tournamentId: Tournament["id"];
  userId: PublicUser["id"];
  userName: string; // Denormalized for list rendering without extra reads
  userPhotoURL: string;
  ownLevel: SkillLevel;
  desiredLevels: SkillLevel[];
  /** Optional free-text note (≤200 chars) — e.g. "looking for B side player". */
  note?: string;
  status: PoolEntryStatus;
  matchedTeamId?: Team["id"]; // Set when status === "matched"
  createdAt: Date;
  updatedAt: Date;
}

// Collection: /poolRequests/{id}
// Interest signal between two free players in the same tournament pool, OR a
// captain-initiated pickup from an existing team. When `fromTeamId` is set the
// request represents the team picking the free player; accept adds the user
// to that team rather than auto-forming a new one.
export interface PoolRequest {
  id: string;
  tournamentId: Tournament["id"];
  fromUserId: PublicUser["id"];
  toUserId: PublicUser["id"];
  fromUserName: string; // Denormalized for inbox rendering
  fromUserPhotoURL: string;
  fromLevel: SkillLevel;
  toLevel: SkillLevel;
  /** Denormalized requester note for inbox rendering. */
  fromNote?: string;
  /** Set when a team captain picks a free player from the pool. */
  fromTeamId?: Team["id"];
  /** Denormalized team name for inbox rendering when fromTeamId is set. */
  fromTeamName?: string;
  status: PoolRequestStatus;
  resultingTeamId?: Team["id"]; // Set when status === "accepted"
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Notification Types
// ============================================================================

export enum NotificationType {
  // Match flow notifications
  CHALLENGE_SENT = "challenge_sent", // Tournament-wide: "Team1 has challenged Team2"
  MATCH_SCHEDULED = "match_scheduled", // Tournament-wide: "Match scheduled for xx.xx.xxxx"
  MATCH_RESULT = "match_result", // Tournament-wide: "Team1 won Team2! 2-1"
  MATCH_CANCELLED = "match_cancelled", // Tournament-wide: "Match cancelled"

  // Other notifications
  WALKOVER = "walkover", // Tournament-wide: "Team gave walkover to Team"
  CHALLENGE_EXPIRED = "challenge_expired",
  CHALLENGE_CANCELLED = "challenge_cancelled",
  CHALLENGE_DECLINED = "challenge_declined", // Braveheart challenge declined by challenged team
  BRAVEHEART_RESPONSE_REMINDER = "braveheart_response_reminder", // Team-scoped reminder that the decline window is running out
  RANK_CHANGED = "rank_changed",
  JOIN_REQUEST = "join_request",
  JOIN_REQUEST_APPROVED = "join_request_approved",
  JOIN_REQUEST_REJECTED = "join_request_rejected",
  TEAM_INVITATION = "team_invitation",
  TEAM_JOINED = "team_joined",
  TEAM_UPDATE = "team_update",
  TEAM_VACATION = "team_vacation",
  MEMBER_JOINED = "member_joined",
  MEMBER_LEFT = "member_left",

  // Organizer notifications
  ORGANIZER_MESSAGE = "organizer_message", // Tournament-wide message from organizer

  // Application notifications
  APPLICATION_MESSAGE = "application_message", // App-wide message from administrator

  // Stream notifications (from striimy.com)
  STREAM_STARTED = "stream_started", // Tournament-wide: "Match live on Striimy"
  STREAM_ENDED = "stream_ended", // Tournament-wide: "Stream ended"

  // Achievement notifications
  ACHIEVEMENT_CHANGED = "achievement_changed", // Tournament-wide: "Team X took the title Y"

  // Player pool notifications
  POOL_REQUEST = "pool_request", // Direct: "Player X is interested in playing with you"
  POOL_TEAM_FORMED = "pool_team_formed", // Team-scoped: "You and X have been matched into a team"
  POOL_PLAYER_JOINED = "pool_player_joined", // Tournament-wide: "Player X joined the free-player pool"
}

/**
 * Herald glyph shown below the Botti von Tornberg avatar on bot messages.
 * Either an emoji (text rendered inside the circle) or an image (e.g. the
 * striimy play logo for stream notifications).
 */
export type HeraldGlyph =
  | { kind: "emoji"; value: string }
  | { kind: "image"; src: string; alt: string };

export enum NotificationVisibility {
  GLOBAL = "global", // Service messages - visible to all users in tournament
  TOURNAMENT = "tournament", // All players in the tournament
  TEAM = "team", // Specific team members only
  APPLICATION = "application", // App-wide messages - visible to all users
}

export interface Notification {
  id: string;
  visibility: NotificationVisibility;
  type: NotificationType;
  titleKey: string; // Translation key for title (e.g., "notifications.matchScheduled.title")
  messageKey: string; // Translation key for message (e.g., "notifications.matchScheduled.messageTournament")
  actionUrl?: string; // Optional link to related page (e.g., match details)

  // Single tournament scope ("__application__" for app-wide notifications)
  tournamentId: Tournament["id"];

  // Optional team scope - only set when visibility is TEAM
  teamId?: Team["id"];

  // Optional metadata for additional context (e.g., team names for match notifications)
  // These values are passed to the translation function
  metadata?: {
    team1Name?: string;
    team2Name?: string;
    team1Id?: string;
    team2Id?: string;
    matchId?: string;
    locationName?: string;
    date?: string;
    team?: string; // For single-team context (e.g., opponent team name)
    winner?: string;
    loser?: string;
    score?: string;
    challengeExpiryDays?: number; // Maximum days ahead a match can be scheduled
    [key: string]: string | number | undefined;
  };

  reactions?: Record<string, ReactionType>; // { [userId]: reactionType }

  createdAt: Date;
  updatedAt?: Date; // Optional for backwards compatibility
}

// Collection: /notificationReadLog/{id}
export interface ReadNotification {
  id: string;
  userId: string;
  notificationId: Notification["id"];
  readAt: Date;
}

// ============================================================================
// Statistics Types
// ============================================================================

export interface TournamentTeamStats {
  teamId: string;
  teamName: string;
  currentLevel: number; // "Nykyinen taso" — current tower level
  startingLevel: number; // "Aloitus taso" — level when first placed in tower
  levelDifference: number; // "Tasoerotus" — startingLevel − currentLevel (positive = climbed up)
  matchesPlayed: number;
  wins: number;
  losses: number;
  walkoverWins: number; // subset of wins decided by walkover
  walkoverLosses: number; // subset of losses decided by walkover
  winRate: number; // 0–100 integer percentage
  setsWon: number;
  setsLost: number;
  setDifference: number; // setsWon - setsLost
  gamesWon: number;
  gamesLost: number;
}

export interface TournamentTopTeamSummary {
  teamId: string;
  teamName: string;
  count: number;
}

export interface TournamentStatistics {
  tournamentId: string;
  teamStats: TournamentTeamStats[];
  // Same shape as teamStats, but walkover matches are excluded from
  // matchesPlayed/wins/losses/winRate (levels are identical in both).
  teamStatsExcludingWalkover: TournamentTeamStats[];
  mostChallenged: TournamentTopTeamSummary | null;
  topChallenger: TournamentTopTeamSummary | null;
  mostDefended: TournamentTopTeamSummary | null;
  mostOwnChallengeWon: TournamentTopTeamSummary | null;
  topBraveheartDecliner: TournamentTopTeamSummary | null;
  mostCardsUsed: TournamentTopTeamSummary | null;
  mostTightWins: TournamentTopTeamSummary | null;
  mostGamesPlayed: TournamentTopTeamSummary | null;
  mostMatchesPlayed: TournamentTopTeamSummary | null;
  braveheartDeclineCount: number;
  rescheduleCount: number;
  generatedAt: Date;
}

/**
 * Persisted snapshot of who currently holds each tournament achievement.
 * Stored at `tournamentAchievements/{tournamentId}` so it can be diffed
 * across recomputations (to fire ACHIEVEMENT_CHANGED) and read live by
 * team cards via onSnapshot.
 *
 * Keys must match the AchievementKey union from `lib/achievements.ts`.
 */
export interface TournamentAchievementHolder {
  teamId: string;
  teamName: string;
  count: number;
  updatedAt: Date;
}

export interface TournamentAchievements {
  tournamentId: string;
  holders: Partial<Record<string, TournamentAchievementHolder>>;
  updatedAt: Date;
}

// ============================================================================
// UI/View Types
// ============================================================================

export interface TowerLevel {
  level: number;
  teams: Team[];
  maxTeams: number;
}

export interface MatchListItem extends Match {
  team1Name: string;
  team2Name: string;
  locationName?: string;
}

// ============================================================================
// Form Types
// ============================================================================

export interface CreateTournamentInput {
  name: string;
  sportId: string;
  description: string;
  logo?: File;
  startsAt: Date;
  endsAt: Date;
  locationIds: string[];
}

export interface CreateTeamInput {
  tournamentId: string;
  name?: string;
  captainId: string;
  playerEmails: string[]; // Max 2 additional players
}

export interface ScheduleMatchInput {
  matchId: string;
  matchDate: Date;
  locationId: string;
}

export interface SubmitPadelResultInput {
  matchId: string;
  set1Team1Games: number;
  set1Team2Games: number;
  set2Team1Games: number;
  set2Team2Games: number;
  tieBreakTeam1Points?: number;
  tieBreakTeam2Points?: number;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// ============================================================================
// Chat Types
// ============================================================================

export enum ChatStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
}

export interface Chat {
  id: string; // Same as matchId for easy lookup
  matchId: string;
  tournamentId: string;
  tournamentSlug: string; // Denormalized for building chat URLs without extra reads
  team1Id: string; // Challenger team
  team2Id: string; // Challenged team
  team1Name: string; // Challenger team name
  team2Name: string; // Challenged team name
  participantIds: string[]; // All player IDs from both teams (for querying)
  status: ChatStatus;
  scheduledArchiveAt?: Date; // When this chat should be archived (set when match completes)
  createdAt: Date;
  updatedAt: Date; // Last message timestamp
  lastMessagePreview?: string; // For list display
  lastMessageSenderId?: string; // For showing sender in preview
  unreadCounts?: Record<string, number>; // { [userId]: unreadCount } - denormalized for efficient queries
}

export interface ChatMessage {
  id: string;
  chatId: string;
  senderId: string;
  senderName: string; // Cached for display
  content: string; // Text only
  createdAt: Date;
  readBy: Record<string, Date>; // userId -> readAt timestamp
  isSystem?: boolean; // True for auto-generated system messages
  imageUrl?: string; // Filled by resize Cloud Function after client upload
  imagePath?: string; // Storage path for the resized image (used for cleanup)
  imagePending?: boolean; // True between client send and function completion
}

export interface ChatTypingStatus {
  id: string; // Usually the chatId
  chatId: string;
  usersTyping: Record<string, Date>; // userId -> last typing timestamp
}

// ============================================================================
// Team Chat Types
// ============================================================================

// Collection: /teamChats/{teamId}
// Private chat for members of a single team. Document ID equals teamId for
// O(1) lookup; participantIds mirrors team.playerIds and is refreshed by the
// sendTeamMessage server action so leavers stop receiving messages on the
// next send.
export interface TeamChat {
  id: string; // = teamId
  teamId: string;
  tournamentId: string;
  tournamentSlug: string; // Denormalized for building chat URLs without extra reads
  teamName: string;
  participantIds: string[];
  status: ChatStatus;
  createdAt: Date;
  updatedAt: Date; // Last message timestamp
  lastMessagePreview?: string;
  lastMessageSenderId?: string;
}

// Collection: /teamChatMessages/{id}
export interface TeamChatMessage {
  id: string;
  teamChatId: string; // = teamId
  senderId: string;
  senderName: string;
  content: string;
  createdAt: Date;
  imageUrl?: string;
  imagePath?: string;
  imagePending?: boolean;
}

// ============================================================================
// Unified Chat Inbox Types
// ============================================================================

// Discriminator for the three chat surfaces that can produce inbox entries.
export type ChatInboxType = "tournament" | "team" | "match";

// Subcollection: /chatInboxes/{userId}/entries/{chatId}
// Per-user denormalized read model maintained by Cloud Function fan-out
// triggers. Powers the unified header bell and grouped chat dropdown without
// requiring a fan-out query across multiple collections on every render.
//
// chatId is:
//   - tournament:  tournamentId
//   - team:        teamId
//   - match:       matchId  (= chats/{chatId})
export interface ChatInboxEntry {
  id: string; // = chatId
  chatId: string;
  type: ChatInboxType;
  tournamentId: string;
  tournamentSlug: string;

  unreadCount: number;
  lastMessageAt?: Date;
  lastMessagePreview?: string;
  lastMessageSenderId?: string;
  lastMessageSenderName?: string;

  // Display labels — populated by the fan-out trigger so the dropdown can
  // render without joining other collections.
  // Tournament: tournamentName.
  // Team:       teamName.
  // Match:      both team names + ids so the UI can pick the opponent.
  displayName?: string;
  team1Id?: string;
  team1Name?: string;
  team2Id?: string;
  team2Name?: string;

  // True once the underlying chat is archived (match completed) or the user
  // is no longer a participant. UI hides archived entries from the bell.
  archived?: boolean;

  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Tournament Chat Types
// ============================================================================

// Collection: /tournamentMessages/{id}
// All messages in the tournament-wide chat — human-authored and bot/herald
// (Botti von Tornberg) system events. Discriminated by `kind`.
export type TournamentMessageKind = "user" | "bot";

export type BotMessageType = NotificationType;

export interface TournamentMessageBotMetadata {
  team1Name?: string;
  team2Name?: string;
  team1Id?: string;
  team2Id?: string;
  matchId?: string;
  locationName?: string;
  date?: string;
  team?: string;
  winner?: string;
  loser?: string;
  score?: string;
  challengeExpiryDays?: number;
  message?: string;
  senderName?: string;
  senderId?: string;
  tournamentName?: string;
  broadcastId?: string;
  organizerBroadcast?: string;
  thumbnailUrl?: string;
  streamUrl?: string;
  achievementKey?: string;
  achievementName?: string;
  challengeType?: string;
  [key: string]: string | number | undefined;
}

export interface TournamentMessage {
  id: string;
  tournamentId: string;
  /** Discriminator: "user" for human-authored, "bot" for herald/system messages. */
  kind?: TournamentMessageKind;
  senderId: string; // Firebase Auth UID of author (for bot: "system")
  senderName: string; // cached display name
  senderTeamId?: string; // for rendering team tag next to the name (user only)
  content: string; // plain text, server-trimmed, 0–1000 chars (empty allowed when imagePending or bot uses titleKey/messageKey)
  reactions?: Record<string, ReactionType>;
  createdAt: Date;
  updatedAt?: Date;
  editedAt?: Date; // set when author edits within the 5-min window (user only)
  deletedAt?: Date; // soft delete; UI renders placeholder (user only)
  deletedBy?: "author" | "organizer";
  imageUrl?: string; // Filled by resize Cloud Function after client upload (user only)
  imagePath?: string; // Storage path for the resized image (used for cleanup) (user only)
  imagePending?: boolean; // True between client send and function completion (user only)

  // Bot/herald-only fields ----------------------------------------------------
  /** Type of bot/herald event — drives icon glyph in BotHeraldMessage. */
  botType?: BotMessageType;
  /** Visibility scope — TOURNAMENT/TEAM/APPLICATION; absent for user messages. */
  visibility?: NotificationVisibility;
  /** Optional team scope when visibility is TEAM. */
  teamId?: Team["id"];
  /** Translation key for headline (e.g. "notifications.matchScheduled.title"). */
  titleKey?: string;
  /** Translation key for body (e.g. "bot.matchScheduled"). */
  messageKey?: string;
  /** Optional link to related page. */
  actionUrl?: string;
  /** Translation interpolation values + flags consumed by BotHeraldMessage. */
  botMetadata?: TournamentMessageBotMetadata;
}

// ============================================================================
// Feedback Types
// ============================================================================

export type FeedbackRating = "awful" | "bad" | "neutral" | "good" | "excellent";

export interface Feedback {
  id: string;
  userId: string;
  comments: string;
  rating: FeedbackRating;
  createdAt: Date;
}

// ============================================================================
// Tournament Survey Types
// ============================================================================

/**
 * A single answer value. Scale questions store 1–5 (or `SURVEY_NOT_APPLICABLE`),
 * choice questions store the option id, text questions store trimmed free text.
 */
export type SurveyAnswerValue = string | number;

export type SurveyAnswers = Record<string, SurveyAnswerValue>;

/**
 * Collection: /tournamentSurveys/{tournamentId}__{userId}
 *
 * One document per participant per tournament. Written incrementally as the
 * respondent answers — a partially filled document (no `completedAt`) is still
 * valuable data, which is the whole point of autosaving.
 */
export interface TournamentSurveyResponse {
  id: string;
  tournamentId: Tournament["id"];
  organizerId: Organizer["id"];
  userId: PublicUser["id"];
  /** UI language the survey was answered in — useful when reading free text. */
  locale: string;
  answers: SurveyAnswers;
  /** Furthest step the respondent reached, so returning resumes in place. */
  lastStepIndex: number;
  /** Set only when the respondent pressed the final submit. */
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Collection: /surveyInvites/{tournamentId}__{userId}__{campaign}
 *
 * Durable ledger of sent survey invite emails, written by
 * scripts/send-survey-invites.js. Kept separate from tournamentSurveys so an
 * unanswered invite never counts as a started response.
 */
export interface SurveyInvite {
  id: string;
  tournamentId: Tournament["id"];
  tournamentSlug: string;
  userId: PublicUser["id"];
  email: string;
  locale: string;
  campaign: string;
  resendId: string | null;
  sentAt: Date;
}
